import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  Text,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react"
import { Global, css } from "@emotion/react"
import axios from "axios"
import React from "react"
import { api } from "config"
import {
  PayPalScriptProvider,
  PayPalCardFieldsProvider,
  PayPalNameField,
  PayPalNumberField,
  PayPalExpiryField,
  PayPalCVVField,
  usePayPalCardFields,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js"
import { Group } from "types/apiTypes"

type PayPalProps = {
  selectedMonthIds: number[]
  voucherDetails?: { code?: string }
  paymentPersonId: number | null
  group?: Group
  isMulti?: boolean
}


const paypalIconFix = css`
  div[id^="paypal-overlay-context-"] {
    pointer-events: none !important;
  }
  .card-icon {
    display: block !important;
    width: 40px;
    height: 24px;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0.75rem;
    left: auto;
  }
  iframe {
    height: 100% !important;
  }
`


/**
 * CSS injected into each PayPal card field iframe so the internal input
 * matches the app's Chakra UI / Lexend design system.
 */
const paypalFieldStyle = {
  input: {
    "font-size": "15px !important",
    "font-family": "Lexend, sans-serif !important",
    color: "#1A202C !important",
    padding: "10px 12px !important",
    outline: "none !important",
  },
  "input::placeholder": {
    color: "#A0AEC0 !important",
  },
  "input:focus": {
    outline: "none !important",
    "border-color": "#3182CE !important",
    "box-shadow": "none !important",
  },
  "input.invalid": {
    color: "#E53E3E !important",
  },
}

/** Must live inside PayPalCardFieldsProvider */
const SubmitButton: React.FC<{
  agreed: boolean
  onError: (msg: string) => void
}> = ({ agreed, onError }) => {
  const { cardFieldsForm } = usePayPalCardFields()
  const [isLoading, setIsLoading] = React.useState(false)

  const handlePay = async () => {
    if (!cardFieldsForm) {
      onError("Card fields not ready. Please try again.")
      return
    }
    setIsLoading(true)
    try {
      await cardFieldsForm.submit()
    } catch (err: any) {
      setIsLoading(false)
      onError(err?.message ?? "Payment failed. Please check your card details.")
    }
  }

  return (
    <Button
      onClick={handlePay}
      isLoading={isLoading}
      isDisabled={!agreed}
      loadingText="Processing…"
      colorScheme="blue"
      width="full"
      size="lg"
    >
      Pay Now
    </Button>
  )
}

/**
 * Warns after 10 s if card fields never initialised (merchant ACDC not enabled).
 * Renders nothing otherwise — field components are always in the DOM.
 */
const EligibilityWarning: React.FC = () => {
  const { cardFieldsForm } = usePayPalCardFields()
  const [timedOut, setTimedOut] = React.useState(false)

  React.useEffect(() => {
    const id = setTimeout(() => {
      if (!cardFieldsForm) setTimedOut(true)
    }, 10000)
    return () => clearTimeout(id)
  }, [cardFieldsForm])

  if (timedOut && !cardFieldsForm) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription>
          Card fields are not available. Please ensure your PayPal merchant
          account has <strong>Advanced Credit and Debit Card Payments</strong>{" "}
          enabled.
        </AlertDescription>
      </Alert>
    )
  }

  return null
}

const CardForm: React.FC<{
  agreed: boolean
  selectedMonthIds: number[]
  voucherDetails?: { code?: string }
  paymentPersonId: number | null
  onError: (msg: string) => void
  onAgreedChange: (v: boolean) => void
}> = ({
  agreed,
  selectedMonthIds,
  voucherDetails,
  paymentPersonId,
  onError,
  onAgreedChange
}) => {
  const [{ isPending, isRejected }] = usePayPalScriptReducer()

  if (isPending) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress isIndeterminate color="blue.500" />
      </Box>
    )
  }

  if (isRejected) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>
          Failed to load PayPal. Please refresh the page and try again.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <PayPalCardFieldsProvider
      createOrder={async () => {
        const r = await axios.post(
          `${api}/payments/create-paypal-order`,
          {
            monthIds: selectedMonthIds,
            coupon: voucherDetails?.code,
            paymentPersonId,
          },
          { withCredentials: true },
        )
        return r.data.orderID as string
      }}
      onApprove={async ({ orderID }: { orderID: string }) => {
        try {
          await axios.post(
            `${api}/payments/capture-paypal-hosted-fields`,
            { orderID },
            { withCredentials: true },
          )
          window.location.href = "/admin/payment-confirmation?paypal=success"
        } catch (err: any) {
          onError(err?.message ?? "Failed to record payment")
        }
      }}
      onError={(err: any) => {
        onError(
          String(err?.message ?? err) ||
            "Payment failed. Please check your card details.",
        )
      }}
    >
      <VStack spacing={4} align="stretch">
        {/* Field components always rendered — PayPal injects iframes into them */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Cardholder Name (කාඩ්පත් හිමියාගේ සම්පුර්ණ නම)
          </FormLabel>
          <PayPalNameField style={paypalFieldStyle} />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Card Number
          </FormLabel>
          <Box position="relative">
            <PayPalNumberField style={paypalFieldStyle} />
          </Box>
        </FormControl>

        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
              Expiry Date
            </FormLabel>
            <PayPalExpiryField style={paypalFieldStyle} />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
              CVV
            </FormLabel>
            <PayPalCVVField style={paypalFieldStyle} />
          </FormControl>
        </Box>

        {/* Shows only if merchant account lacks ACDC after 10 s */}
        <EligibilityWarning />

        <Box display="flex" justifyContent="center">
          <Checkbox
            isChecked={agreed}
            onChange={(e) => onAgreedChange(e.target.checked)}
          >
            I agree to{" "}
            <a
              href="/admin/terms-and-conditions"
              target="_blank"
              rel="noreferrer"
            >
              <Text as="span" color="blue.500" textDecoration="underline">
                Terms and Conditions
              </Text>
            </a>{" "}
            and{" "}
            <a href="/admin/privacy-policy" target="_blank" rel="noreferrer">
              <Text as="span" color="blue.500" textDecoration="underline">
                Privacy Policy
              </Text>
            </a>
            . මෙම කොටුව මත ක්ලික් කරන්න
          </Checkbox>
        </Box>

        <SubmitButton agreed={agreed} onError={onError} />
      </VStack>
    </PayPalCardFieldsProvider>
  )
}

const PayPal = ({
  selectedMonthIds,
  voucherDetails,
  paymentPersonId
}: PayPalProps) => {
  const [agreed, setAgreed] = React.useState(false)
  const [clientToken, setClientToken] = React.useState<string | null>(null)
  const [clientId, setClientId] = React.useState<string | null>(null)
  const [tokenError, setTokenError] = React.useState(false)
  const toast = useToast()

  React.useEffect(() => {
    axios
      .get(`${api}/payments/paypal-client-token`, { withCredentials: true })
      .then((r) => {
        setClientToken(r.data.clientToken)
        setClientId(r.data.clientId)
      })
      .catch(() => setTokenError(true))
  }, [])

  const showError = (msg: string) =>
    toast({
      title: "Payment Failed",
      description: msg || "Please check your card details and try again.",
      status: "error",
      duration: 6000,
      isClosable: true
    })

  return (
    <>
      <Global styles={paypalIconFix} />
      <Box my={4}>
        <Text textAlign="center" fontSize="2xl" fontWeight="bold" mb={6}>
          Pay with Credit or Debit Card
        </Text>

        {tokenError ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              Unable to initialize PayPal. Please refresh and try again.
            </AlertDescription>
          </Alert>
        ) : !clientToken || !clientId ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress isIndeterminate color="blue.500" />
          </Box>
        ) : (
          <PayPalScriptProvider
            options={{
              clientId,
              dataClientToken: clientToken,
              components: "card-fields",
              intent: "capture",
              currency: "EUR"
            }}
          >
            <CardForm
              agreed={agreed}
              selectedMonthIds={selectedMonthIds}
              voucherDetails={voucherDetails}
              paymentPersonId={paymentPersonId}
              onError={showError}
              onAgreedChange={setAgreed}
            />
          </PayPalScriptProvider>
        )}
      </Box>
    </>
  )
}

export default PayPal
